import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import useAxios from "../../../../../../hooks/useAxios";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../../shared/components/dialog/Dialog";
import { Label } from "../../../../../../shared/components/label/Label";
import { Input } from "../../../../../../shared/components/input/Input";
import { Switch } from "../../../../../../shared/components/switch/Switch";
import { Textarea } from "../../../../../../shared/components/textarea/Textarea";
import { Button } from "../../../../../../shared/components/button/Button";
import { API_DATASETS } from "../../../../../../shared/lib/apiEndpointsResolver";
import UnexpectedBackendError from "../../../../../../shared/components/UnexpectedBackendError";

const datasetFormInitialState = {
  code: "",
  name: { en: "", es: "" },
  description: { en: "", es: "" },
  active: true,
  etlQuery: "",
};

const AddDatasetDialog = ({
  isDatasetDialogOpen,
  setIsDatasetDialogOpen,
  onAddDatasetSuccessfully,
  checkDatasetCodeDuplicated,
}) => {
  const { t } = useTranslation();
  const axios = useAxios();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [datasetForm, setDatasetForm] = useState(datasetFormInitialState);
  const [unexpectedError, setUnexpectedError] = useState(false);

  const validateDatasetForm = (form) => {
    const errors = {};

    if (!form.code.trim()) {
      errors.code = t("datasetsCentre.common.codeRequired");
    } else if (checkDatasetCodeDuplicated(form.code.trim())) {
      errors.code = t("datasetsCentre.common.codeExists");
    }

    if (!form.name.en.trim()) {
      errors.nameEn = t("datasetsCentre.common.nameEnRequired");
    }

    if (!form.name.es.trim()) {
      errors.nameEs = t("datasetsCentre.common.nameEsRequired");
    }

    if (!form.description.en.trim()) {
      errors.descriptionEn = t("datasetsCentre.common.descriptionEnRequired");
    }

    if (!form.description.es.trim()) {
      errors.descriptionEs = t("datasetsCentre.common.descriptionEsRequired");
    }

    if (!form.etlQuery.trim()) {
      errors.sqlSentence = t("datasetsCentre.datasets.etlQueryRequired");
    }

    return errors;
  };

  const handleAddDataset = async () => {
    const errors = validateDatasetForm(datasetForm);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await axios.post(API_DATASETS, datasetForm);

      if (result.status === 200) {
        onAddDatasetSuccessfully(result.data);
        closeDialog();
      } else {
        console.error(result.data);
        setUnexpectedError(true);
      }
    } catch (error) {
      console.error(error);
      setUnexpectedError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeDialog = () => {
    setIsDatasetDialogOpen(false);
    setDatasetForm(datasetFormInitialState);
    setFormErrors({});
  };

  return (
    <Dialog open={isDatasetDialogOpen} onOpenChange={setIsDatasetDialogOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add New Dataset</DialogTitle>
          <DialogDescription>
            Create a new dataset configuration. Fill in all required fields.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {unexpectedError && <UnexpectedBackendError />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dataset-code">Code *</Label>
              <Input
                id="dataset-code"
                value={datasetForm.code}
                onChange={(e) =>
                  setDatasetForm((prev) => ({ ...prev, code: e.target.value }))
                }
                placeholder="dataset_code"
                className={formErrors.code ? "border-destructive" : ""}
              />
              {formErrors.code && (
                <p className="text-sm text-destructive">{formErrors.code}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dataset-enabled">Pipeline Status</Label>
              <div className="flex items-center space-x-2">
                <Switch
                  id="dataset-enabled"
                  checked={datasetForm.enabled}
                  onCheckedChange={(checked) =>
                    setDatasetForm((prev) => ({ ...prev, enabled: checked }))
                  }
                />
                <Label
                  htmlFor="dataset-enabled"
                  className="text-sm font-normal"
                >
                  {datasetForm.enabled
                    ? "Enabled in pipeline"
                    : "Disabled from pipeline"}
                </Label>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dataset-name-en">Name (English) *</Label>
              <Input
                id="dataset-name-en"
                value={datasetForm.name.en}
                onChange={(e) =>
                  setDatasetForm((prev) => ({
                    ...prev,
                    name: { ...prev.name, en: e.target.value },
                  }))
                }
                placeholder="Dataset name in English"
                className={formErrors.nameEn ? "border-destructive" : ""}
              />
              {formErrors.nameEn && (
                <p className="text-sm text-destructive">{formErrors.nameEn}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dataset-name-es">Name (Spanish) *</Label>
              <Input
                id="dataset-name-es"
                value={datasetForm.name.es}
                onChange={(e) =>
                  setDatasetForm((prev) => ({
                    ...prev,
                    name: { ...prev.name, es: e.target.value },
                  }))
                }
                placeholder="Dataset name in Spanish"
                className={formErrors.nameEs ? "border-destructive" : ""}
              />
              {formErrors.nameEs && (
                <p className="text-sm text-destructive">{formErrors.nameEs}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dataset-desc-en">Description (English)</Label>
              <Textarea
                id="dataset-desc-en"
                value={datasetForm.description.en}
                onChange={(e) =>
                  setDatasetForm((prev) => ({
                    ...prev,
                    description: { ...prev.description, en: e.target.value },
                  }))
                }
                placeholder="Dataset description in English"
                rows={3}
              />
              {formErrors.descriptionEn && (
                <p className="text-sm text-destructive">
                  {formErrors.descriptionEn}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="dataset-desc-es">Description (Spanish)</Label>
              <Textarea
                id="dataset-desc-es"
                value={datasetForm.description.es}
                onChange={(e) =>
                  setDatasetForm((prev) => ({
                    ...prev,
                    description: { ...prev.description, es: e.target.value },
                  }))
                }
                placeholder="Dataset description in Spanish"
                rows={3}
              />
              {formErrors.descriptionEs && (
                <p className="text-sm text-destructive">
                  {formErrors.descriptionEs}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="dataset-sql">SQL Query *</Label>
            <Textarea
              id="dataset-sql"
              value={datasetForm.etlQuery}
              onChange={(e) =>
                setDatasetForm((prev) => ({
                  ...prev,
                  etlQuery: e.target.value,
                }))
              }
              placeholder="SELECT * FROM table_name"
              rows={4}
              className={`font-mono text-sm ${formErrors.etlQuery ? "border-destructive" : ""}`}
            />
            {formErrors.sqlSentence && (
              <p className="text-sm text-destructive">{formErrors.etlQuery}</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsDatasetDialogOpen(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleAddDataset} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Dataset"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddDatasetDialog;
